import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Shield, FileText, Calendar } from 'lucide-react';

const BODY_PARTS = [
  'Hamstring', 'Quadriceps', 'Ankle', 'Knee', 'Groin', 'Calf', 'Hip',
  'Shoulder', 'Lower Back', 'Foot', 'Thigh', 'Achilles', 'Head', 'Other'
];

const INJURY_TYPES = [
  'Muscle Strain', 'Ligament Sprain', 'Tendinopathy', 'Fracture', 'Contusion',
  'Dislocation', 'Concussion', 'Overuse', 'ACL Reconstruction', 'Meniscus',
  'Hamstring Strain', 'Lateral Ankle Sprain', 'Other'
];

const STATUS_STYLES = {
  RECOVERED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  ACTIVE: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  CHRONIC: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

const InjuryHistory = ({ athleteId }) => {
  const { api, hasRole } = useAuth();
  const { language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState({
    injury_type: '', body_part: '', date: '', end_date: '', duration_days: '', status: 'RECOVERED', notes: ''
  });

  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get(`/injury-history/${athleteId}`);
      setEntries(res.data);
    } catch (err) {
      console.error('Failed to fetch injury history:', err);
    } finally {
      setLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const resetForm = () => {
    setForm({ injury_type: '', body_part: '', date: '', end_date: '', duration_days: '', status: 'RECOVERED', notes: '' });
    setEditEntry(null);
    setShowCreate(false);
  };

  const handleSave = async () => {
    if (!form.injury_type || !form.body_part || !form.date) {
      toast.error(language === 'IT' ? 'Campi obbligatori mancanti' : 'Missing required fields');
      return;
    }
    try {
      const payload = {
        ...form,
        athlete_id: athleteId,
        duration_days: form.duration_days ? parseInt(form.duration_days) : null,
        end_date: form.end_date || null,
        notes: form.notes || null,
      };
      if (editEntry) {
        const { athlete_id, ...updatePayload } = payload;
        await api.put(`/injury-history/${editEntry.id}`, updatePayload);
        toast.success(language === 'IT' ? 'Infortunio aggiornato' : 'Injury updated');
      } else {
        await api.post('/injury-history', payload);
        toast.success(language === 'IT' ? 'Infortunio registrato' : 'Injury recorded');
      }
      resetForm();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await api.delete(`/injury-history/${entryId}`);
      toast.success(language === 'IT' ? 'Eliminato' : 'Deleted');
      fetchHistory();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (entry) => {
    setForm({
      injury_type: entry.injury_type || '',
      body_part: entry.body_part || '',
      date: entry.date || '',
      end_date: entry.end_date || '',
      duration_days: entry.duration_days || '',
      status: entry.status || 'RECOVERED',
      notes: entry.notes || '',
    });
    setEditEntry(entry);
    setShowCreate(true);
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm" data-testid="injury-history-section">
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-rose-400" />
          <h2 className="text-lg font-semibold text-zinc-100">
            {language === 'IT' ? 'Storico Infortuni' : 'Injury History'}
          </h2>
          <span className="text-xs text-zinc-500 ml-1">({entries.length})</span>
        </div>
        {canEdit && (
          <Button
            onClick={() => { resetForm(); setShowCreate(true); }}
            size="sm"
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            data-testid="add-injury-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> {language === 'IT' ? 'Aggiungi' : 'Add'}
          </Button>
        )}
      </div>

      <div className="p-4">
        {loading ? (
          <div className="text-sm text-zinc-500 py-2">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4 text-center">
            {language === 'IT' ? 'Nessun infortunio registrato' : 'No injuries recorded'}
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-start justify-between p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm"
                data-testid={`injury-entry-${entry.id}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`p-1.5 rounded-sm mt-0.5 ${entry.source === 'RTP' ? 'bg-purple-500/15' : 'bg-zinc-800'}`}>
                    {entry.source === 'RTP' ? (
                      <Shield className="w-4 h-4 text-purple-400" />
                    ) : (
                      <Calendar className="w-4 h-4 text-zinc-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-100 font-medium text-sm">{entry.injury_type}</span>
                      <span className="text-zinc-500 text-xs">({entry.body_part})</span>
                      <span className={`px-2 py-0.5 text-xs rounded border ${STATUS_STYLES[entry.status] || 'bg-zinc-700 text-zinc-400 border-zinc-600'}`}>
                        {entry.status}
                      </span>
                      {entry.source === 'RTP' && (
                        <span className="px-2 py-0.5 text-xs bg-purple-500/10 text-purple-400 rounded border border-purple-500/20">
                          RTP {entry.current_phase?.replace('_', ' ') || ''}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                      <span>{entry.date}{entry.end_date ? ` - ${entry.end_date}` : ''}</span>
                      {entry.duration_days && <span>{entry.duration_days}d</span>}
                    </div>
                    {entry.notes && (
                      <p className="text-xs text-zinc-400 mt-1">{entry.notes}</p>
                    )}
                  </div>
                </div>
                {canEdit && entry.source === 'MANUAL' && (
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <button onClick={() => openEdit(entry)} className="p-1.5 text-zinc-500 hover:text-amber-400 transition-colors" data-testid={`edit-injury-${entry.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(entry.id)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors" data-testid={`delete-injury-${entry.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">
              {editEntry
                ? (language === 'IT' ? 'Modifica Infortunio' : 'Edit Injury')
                : (language === 'IT' ? 'Nuovo Infortunio' : 'New Injury')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Tipo' : 'Type'} *</Label>
                <Select value={form.injury_type} onValueChange={(v) => setForm({ ...form, injury_type: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="injury-type-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {INJURY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Parte del corpo' : 'Body Part'} *</Label>
                <Select value={form.body_part} onValueChange={(v) => setForm({ ...form, body_part: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="body-part-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {BODY_PARTS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Data inizio' : 'Start Date'} *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="injury-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Data fine' : 'End Date'}</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="injury-end-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Durata (giorni)' : 'Duration (days)'}</Label>
                <Input type="number" min="0" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="injury-duration" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="injury-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="RECOVERED">{language === 'IT' ? 'Recuperato' : 'Recovered'}</SelectItem>
                    <SelectItem value="ACTIVE">{language === 'IT' ? 'Attivo' : 'Active'}</SelectItem>
                    <SelectItem value="CHRONIC">{language === 'IT' ? 'Cronico' : 'Chronic'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">{language === 'IT' ? 'Note' : 'Notes'}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-950 border-zinc-800" rows={2} data-testid="injury-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm} className="border-zinc-700">{language === 'IT' ? 'Annulla' : 'Cancel'}</Button>
            <Button onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="save-injury-btn">
              {editEntry ? (language === 'IT' ? 'Aggiorna' : 'Update') : (language === 'IT' ? 'Salva' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InjuryHistory;
